import type {
  AgingBucket,
  EmailActivityFilter,
  InvoiceTypeFilter,
  RiskLevel,
  RiskLevelFilters,
} from "./types";

type RiskFilterOption = {
  value: AgingBucket;
  label: string;
  dotClass?: string;
  textClass?: string;
};

type InvoiceTypeFilterOption = {
  value: InvoiceTypeFilter;
  label: string;
};

/**
 * Aging chip colors, matching the validated scale in
 * `utils/receivablesChartTokens` so the filter dots read as the same
 * language as the charts.
 *
 * The dot carries the color; the label stays in neutral ink. The lighter
 * ramp steps sit below 3:1 on white and would be illegible as text — and a
 * colored mark beside a label is the identity channel anyway.
 */
export const AGING_BUCKET_COLORS: Record<
  Exclude<RiskLevel, "all">,
  { dotClass: string; textClass: string }
> = {
  current: { dotClass: "bg-[#2a78d6]", textClass: "text-slate-900" },
  "0_30": { dotClass: "bg-[#EE9A4D]", textClass: "text-slate-900" },
  "31_60": { dotClass: "bg-[#D4762A]", textClass: "text-slate-900" },
  "61_90": { dotClass: "bg-[#A85615]", textClass: "text-slate-900" },
  "90_plus": { dotClass: "bg-[#733609]", textClass: "text-slate-900" },
};

export const AGING_BUCKET_FILTER_OPTIONS: RiskFilterOption[] = [
  {
    value: "current",
    label: "Current",
    ...AGING_BUCKET_COLORS.current,
  },
  {
    value: "0_30",
    label: "0–30",
    ...AGING_BUCKET_COLORS["0_30"],
  },
  {
    value: "31_60",
    label: "31–60",
    ...AGING_BUCKET_COLORS["31_60"],
  },
  {
    value: "61_90",
    label: "61–90",
    ...AGING_BUCKET_COLORS["61_90"],
  },
  {
    value: "90_plus",
    label: "90+",
    ...AGING_BUCKET_COLORS["90_plus"],
  },
];

export const INVOICE_TYPE_FILTER_OPTIONS: InvoiceTypeFilterOption[] = [
  { value: "all", label: "All" },
  { value: "PI", label: "PI" },
  { value: "3RD", label: "3RD" },
];

/** Options for the Email column filter (invoices view only). */
export const EMAIL_ACTIVITY_FILTER_OPTIONS: {
  value: EmailActivityFilter;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft ready" },
  { value: "unread", label: "Unread reply" },
  { value: "none", label: "No activity" },
];

export function toggleRiskLevelFilter(
  current: RiskLevelFilters,
  bucket: AgingBucket | "all",
): RiskLevelFilters {
  if (bucket === "all") {
    return [];
  }
  if (current.includes(bucket)) {
    return current.filter((value) => value !== bucket);
  }
  return [...current, bucket];
}

export function riskIndicatorClass(
  riskLevel: Exclude<RiskLevel, "all">,
): string {
  return AGING_BUCKET_COLORS[riskLevel]?.textClass ?? "text-green-600";
}
